import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Field, formValueSelector } from "redux-form";
import injectSheet from "lib/sheet";

const Checkbox = ({ input, label, description, disabled }) => (
  <label
    style={{
      display: "flex",
      alignItems: "flex-start",
      marginBottom: 16,
      cursor: disabled ? "not-allowed" : "pointer",
    }}
  >
    <input
      type="checkbox"
      checked={input.value}
      onChange={input.onChange}
      onBlur={input.onBlur}
      onFocus={input.onFocus}
      disabled={disabled}
      style={{
        marginTop: 4,
        marginRight: 12,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    />
    <div>
      <div style={{ fontWeight: 500, marginBottom: 4 }}>{label}</div>
      {description && (
        <div style={{ fontSize: 13, color: "#6b7280" }}>{description}</div>
      )}
    </div>
  </label>
);

Checkbox.propTypes = {
  input: PropTypes.object,
  label: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
};

const FeatureDisplay = ({ feature }) => (
  <label
    style={{
      display: "flex",
      alignItems: "flex-start",
      marginBottom: 16,
    }}
  >
    <input
      type="checkbox"
      checked={feature.enabled}
      readOnly
      disabled
      style={{
        marginTop: 4,
        marginRight: 12,
        cursor: "not-allowed",
      }}
    />
    <div>
      <div style={{ fontWeight: 500, marginBottom: 4 }}>{feature.name}</div>
      {feature.description && (
        <div style={{ fontSize: 13, color: "#6b7280" }}>
          {feature.description}
        </div>
      )}
    </div>
  </label>
);

FeatureDisplay.propTypes = {
  feature: PropTypes.object,
};

const CustomerFeatures = ({ rowData, classes, currentSeatTypeId }) => {
  const features = rowData?.get ? rowData.get("features") : rowData?.features;
  const locked = rowData?.get ? rowData.get("locked") : rowData?.locked;
  // Memoize the initial seat type from first render: prefer rowData, fallback to currentSeatTypeId
  const initialSeatTypeId = useMemo(() => {
    const idFromRow = rowData?.get
      ? rowData.get("seatTypeId")
      : rowData?.seatTypeId;
    return idFromRow ?? currentSeatTypeId ?? null;
  }, []);

  // Check if seat type has changed
  const seatTypeChanged =
    !locked &&
    currentSeatTypeId &&
    initialSeatTypeId !== null &&
    currentSeatTypeId !== initialSeatTypeId;

  if (!features) {
    return <div className={classes.empty}>No features available</div>;
  }

  // Convert Immutable to JS if needed
  const featuresArray = features?.toJS ? features.toJS() : features;

  if (!featuresArray || featuresArray.length === 0) {
    return <div className={classes.empty}>No features available</div>;
  }

  // If locked (read-only mode), render without Field components
  if (locked) {
    return (
      <div className={classes.container}>
        {featuresArray.map((feature) => (
          <FeatureDisplay key={feature.id} feature={feature} />
        ))}
      </div>
    );
  }

  // If not locked (edit mode), render with Field components
  return (
    <div className={classes.container}>
      {featuresArray.map((feature, index) => (
        <div key={feature.id}>
          <Field
            name={`features[${index}].id`}
            component={({ input }) => <input {...input} type="hidden" />}
          />
          <Field
            name={`features[${index}].enabled`}
            component={Checkbox}
            label={feature.name}
            description={feature.description}
            disabled={seatTypeChanged}
          />
        </div>
      ))}
    </div>
  );
};

CustomerFeatures.propTypes = {
  rowData: PropTypes.object,
  classes: PropTypes.object,
  currentSeatTypeId: PropTypes.number,
};

const sheet = {
  container: {
    padding: "16px 0",
  },
  empty: {
    color: "#9ca3af",
    fontStyle: "italic",
    padding: "16px 0",
  },
};

// Connect to Redux Form to watch seatTypeId changes
const selector = formValueSelector("customerForm");
const mapStateToProps = (state) => ({
  currentSeatTypeId: selector(state, "seatTypeId"),
});

export default connect(mapStateToProps)(injectSheet(sheet)(CustomerFeatures));
